import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import additionSentencePartSvg from '../../../assets/img/addition_sentence_part.svg';
import circumstanceSentencePartSvg from '../../../assets/img/circumstance_sentence_part.svg';
import definitionSentencePartSvg from '../../../assets/img/definition_sentence_part.svg';
import clearSvg from '../../../assets/img/format-clear.svg';
import predicateSentencePartSvg from '../../../assets/img/predicate_sentence_part.svg';
import subjectSentencePartSvg from '../../../assets/img/subject_sentence_part.svg';
import { getUserType } from '../../../redux/slices/userSlice';
import { ICL, SentencePart, SentenceStructureTask, TaskKeysType } from '../../../shared/types';
import { WordOrderPartEnum } from '../../../shared/types/task';
import StudentTaskWrapper from '../StudentTaskWrapper';
import './style.scss';

interface StudentTextTaskProps extends SentenceStructureTask {
  icl: ICL | null;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | string[] | boolean | SentencePart[]) => void;
  saveTaskAnswer: (id: string) => Promise<void>;
  hw?: boolean;
  preview?: boolean;
  studentName?: string;
  index: number;
}

const buttonsPartObj: Record<
  string,
  {
    name: WordOrderPartEnum;
    icon: string;
  }
> = {
  subjectSP: { name: WordOrderPartEnum.subject, icon: subjectSentencePartSvg },
  predicate: { name: WordOrderPartEnum.predicate, icon: predicateSentencePartSvg },
  definition: { name: WordOrderPartEnum.definition, icon: definitionSentencePartSvg },
  addition: { name: WordOrderPartEnum.addition, icon: additionSentencePartSvg },
  circumstance: { name: WordOrderPartEnum.circumstance, icon: circumstanceSentencePartSvg },
};

export default function StudentSentenceStructureTask({
  icl,
  text,
  subText,
  answer,
  hasAnswer,
  hasStar,
  checkedAt,
  completedAt,
  handleChangeTaskByKey,
  id,
  hasPhoto,
  answersPhotos,
  saveTaskAnswer,
  deadline,
  audioUrl,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  sentence,
  sentenceParts,
  studentName,
  index,
}: StudentTextTaskProps) {
  const userType = useSelector(getUserType);
  const { t } = useTranslation();
  const [targetWord, setTargetWord] = useState('');
  const [targetPart, setTargetPart] = useState('');

  useEffect(() => {
    addSentencePartToWord(sentence, targetWord, targetPart);
  }, [targetPart, targetWord]);

  const renderSentence = (sentence: string) => {
    return sentence.split(' ').map((word, index) => {
      return (
        <div
          key={index}
          onClick={() => handleSetWord(word)}
          className={`${getSentencePartNameForWord(word, index) ? `${getSentencePartNameForWord(word, index)}-underline` : ''} mr-1.5 inline-block ${targetWord === word ? 'rounded-[4px] border-[1px] border-[#52C7FA] bg-[#DCF0FF]' : ''}`}
        >
          {word}
        </div>
      );
    });
  };

  const getSentencePartNameForWord = (word: string, index: number) => {
    if (sentenceParts) {
      const targetWord = sentenceParts.find(it => it.word === word && it.index === index);

      return targetWord?.part;
    }
  };

  const renderPartsButton = () => {
    return Object.keys(buttonsPartObj).map(key => {
      return (
        <Tooltip
          key={key}
          title={t(key)}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className={`btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600 ${targetPart === buttonsPartObj[key].name ? '!bg-[#52C7FA]' : ''}`}
            onClick={() => handleSetPart(buttonsPartObj[key].name)}
          >
            <img src={buttonsPartObj[key].icon} alt="mic" />
          </button>
        </Tooltip>
      );
    });
  };

  const handleSetWord = (word: string) => {
    if (userType === 'Student') {
      setTargetWord(word);
    }
  };

  const handleSetPart = (part: WordOrderPartEnum | 'clear') => {
    if (userType === 'Student') {
      setTargetPart(part);
    }
  };

  const addSentencePartToWord = (sentence: string, word: string, part: string) => {
    if (!sentenceParts) {
      const words = sentence.split(' ');
      const newSentenceParts: SentencePart[] = words.map((w, i) => {
        if (w === word)
          return {
            word: w,
            index: i,
            part: part === 'clear' ? WordOrderPartEnum.clear : (part as WordOrderPartEnum),
          };
        else return { word: w, index: i, part: WordOrderPartEnum.clear };
      });

      handleChangeTaskByKey(id, 'sentenceParts', newSentenceParts);
    } else {
      const newSentenceParts: SentencePart[] = sentenceParts.map(it => {
        if (it.word === word) return { ...it, part: part as WordOrderPartEnum };
        else return it;
      });

      handleChangeTaskByKey(id, 'sentenceParts', newSentenceParts);
    }
  };

  const isDisabledSubmitButton = useMemo(
    () => !sentenceParts?.length || !!completedAt || !!checkedAt,
    [sentenceParts, completedAt, checkedAt],
  );

  return (
    <StudentTaskWrapper
      icl={icl}
      taskText={text}
      subText={subText}
      answersPhotos={answersPhotos}
      id={id}
      handleChangeTaskByKey={handleChangeTaskByKey}
      checkedAt={checkedAt}
      hasPhoto={hasPhoto}
      completedAt={completedAt}
      saveTaskAnswer={saveTaskAnswer}
      deadline={deadline}
      audioUrl={audioUrl}
      answer={answer}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      videoUrl={videoUrl}
      taskPhotos={taskPhotos}
      editedAnswerPhotos={editedAnswerPhotos}
      hw={hw}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      preview={preview}
      isDisabledSubmitButton={isDisabledSubmitButton}
      studentName={studentName}
      isSubmitButton
      index={index}
    >
      <>
        <div className="py-4 lg:py-8">
          <div className="flex flex-wrap">{renderSentence(sentence)}</div>

          {userType === 'Student' && (
            <div className="mt-4 flex flex-wrap gap-3">
              {renderPartsButton()}

              <Tooltip
                title={t('clear')}
                componentsProps={{
                  popper: {
                    sx: {
                      ['& .MuiTooltip-arrow']: {
                        color: '#9CDFFC',
                      },
                      ['& .MuiTooltip-tooltip']: {
                        backgroundColor: '#9CDFFC',
                        color: '#000000',
                        fontSize: '14px',
                        padding: '14px',
                        borderRadius: '8px',
                      },
                    },
                  },
                }}
                arrow
              >
                <button
                  className={`btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600 ${targetPart === 'clear' ? '!bg-[#52C7FA]' : ''}`}
                  onClick={() => handleSetPart('clear')}
                >
                  <img src={clearSvg} alt={'clear'} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      </>
    </StudentTaskWrapper>
  );
}
